import { FC, ReactNode } from 'react';

import ButtonBase from '@material-ui/core/ButtonBase';
import Typography from '@material-ui/core/Typography';

import type { ExerciseAnswer } from 'api/rest/exercises/exercises.types';

import type { ExerciseSelectionMode } from './groupExerciseAnswers';
import OnGoGroupedMultiSelectChoices from './OnGoGroupedMultiSelectChoices';
import OnGoMultiSelectChoices from './OnGoMultiSelectChoices';
import OnGoTrueFalseChoices from './OnGoTrueFalseChoices';

type OnGoExerciseChoicesProps = {
  answers: ExerciseAnswer[];
  correctAnswerIdSet: Set<number>;
  disabled: boolean;
  getSingleChoiceClassName: (answer: ExerciseAnswer) => string;
  isAttemptCorrect: boolean | null;
  isResultPhase: boolean;
  onSelectSingleChoice: (answerId: number) => void;
  onSelectTrueFalseChoice: (answerId: number, group: number) => void;
  onToggleMultiSelectChoice: (answerId: number) => void;
  phase: 'question' | 'result';
  selectedAnswerIdSet: Set<number>;
  selectedAnswerIds: number[];
  selectionMode: ExerciseSelectionMode;
};

const OnGoExerciseChoices: FC<OnGoExerciseChoicesProps> = ({
  answers,
  correctAnswerIdSet,
  disabled,
  getSingleChoiceClassName,
  isAttemptCorrect,
  isResultPhase,
  onSelectSingleChoice,
  onSelectTrueFalseChoice,
  onToggleMultiSelectChoice,
  phase,
  selectedAnswerIdSet,
  selectedAnswerIds,
  selectionMode,
}) => {
  const sharedChoiceProps = {
    answers,
    correctAnswerIdSet,
    disabled,
    isAttemptCorrect,
    isResultPhase,
    selectedAnswerIds,
  };

  let choices: ReactNode;

  switch (selectionMode) {
    case 'trueFalse':
      choices = <OnGoTrueFalseChoices {...sharedChoiceProps} onSelect={onSelectTrueFalseChoice} />;
      break;
    case 'multiSelect':
      choices = <OnGoMultiSelectChoices {...sharedChoiceProps} onToggle={onToggleMultiSelectChoice} />;
      break;
    case 'groupedMultiSelect':
      choices = <OnGoGroupedMultiSelectChoices {...sharedChoiceProps} onToggle={onToggleMultiSelectChoice} />;
      break;
    default:
      choices = answers.map(answer => (
        <ButtonBase
          key={answer.id}
          aria-pressed={phase === 'question' ? selectedAnswerIdSet.has(answer.id) : undefined}
          className={getSingleChoiceClassName(answer)}
          disabled={phase === 'result' || disabled}
          onClick={() => {
            switch (phase) {
              case 'question':
                onSelectSingleChoice(answer.id);
                break;
              case 'result':
                break;
            }
          }}
        >
          <Typography>{answer.label}</Typography>
        </ButtonBase>
      ));
      break;
  }

  return <>{choices}</>;
};

export default OnGoExerciseChoices;
